import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import {
  CellStyled,
  CenterCellStyled,
  FlexContainer,
  ImageContainerStyled,
  ImageRowContainer,
  InfoWithImage,
  LinkWrapper,
  DoubleBigCardContainer,
  RepeaterWrapper,
  SingleBigCardContainer,
  StyledLink,
  StyledMobileLink,
  TableBodyRowCellStyled,
  TableBodyRowStyled,
  TableStyled,
} from './EventsTable.style';
import {
  ContentWrapper,
  HideOnDesktop,
  HideOnMobile,
} from '../../../styles/shared';
import {
  TableBody,
  TableBodyRowCell,
  TableHeader,
} from '../../../components/table/Table';
import { getStrapiMedia } from '../../../utils/media';
import PaginationArrows from '../../../components/pagination-arrows/PaginationArrows';
import CardEventMobile, {
  CardEventData,
} from '../../../components/cards/card-event-mobile/CardEventMobile';
import Repeater from '../../../components/repeater/Repeater';
import Link from 'next/link';
import SectionHeader from '../../../components/section-header/SectionHeader';
import { Event } from '../../../models/auto-generated-strapi-types/types/Event';
import { normalizeAttributesNewsEvents } from '../../../utils/event';
import { getEvents } from '../../../utils/api';
import { GlobalResponse, Slug } from '../../../models/domain/interfaces';
import { stringToISODate } from 'corporate-utils';
import CardEvent from '../../../components/cards/card-event/CardEvent';
import { EventCardType } from '../../../models/enum';
import { fromHumanNumberOfColumnsToRepeaterNumberOfColumns } from '../../../utils/carousels';
import { useWindowSize } from 'usehooks-ts';
import { useTheme } from '@mui/material';
import { getCompany } from '../../../utils/company';
import { Companies } from 'corporate-types';
import { DEFAULT_IMAGE } from '../../../models/domain/const';
import ImageWithFallback from '../../../components/image-with-fallback/ImageWithFallback';

interface SliderEventsProps {
  mainTitle: string;
  title: string;
  mainTitleFontSize?: number;
  titleFontSize?: number;
  mainTitleMobileFontSize?: number;
  titleMobileFontSize?: number;
  mainTitleFontWeight?: number;
  mainTitleMobileFontWeight?: number;
  titleFontWeight?: number;
  titleMobileFontWeight?: number;
  events: Event[];
}

const INITIAL_MOBILE_ITEM_COUNT = 4;
const INITIAL_ITEM_COUNT = 3;

const SliderEvents: React.FC<any> = ({
  data,
  translations,
  isPrerenderRequest,
}: {
  data: SliderEventsProps;
  translations: any;
  isPrerenderRequest: boolean;
}) => {
  const {
    mainTitle,
    title,
    events,
    mainTitleFontSize,
    titleFontSize,
    titleMobileFontSize,
    mainTitleMobileFontSize,
    mainTitleFontWeight,
    mainTitleMobileFontWeight,
    titleFontWeight,
    titleMobileFontWeight,
  } = data ?? {};

  const [currentPage, setCurrentPage] = useState(0);
  const [mobileItemsCount, setMobileItemsCount] = useState(
    INITIAL_MOBILE_ITEM_COUNT
  );
  const pageSize = 6;
  const startIndex = currentPage * pageSize;
  const endIndex = (currentPage + 1) * pageSize;

  const slidePrev = () => setCurrentPage(currentPage - 1);
  const slideNext = () => setCurrentPage(currentPage + 1);

  const router = useRouter();

  const { width } = useWindowSize();
  const theme = useTheme();

  const [columns, setColumns] = useState<number>(INITIAL_ITEM_COUNT);

  useEffect(() => {
    switch (true) {
      case width < theme.breakpointsMediaQuery.values.md &&
        width >= theme.breakpointsMediaQuery.values.sm:
        if (columns !== 2) {
          setColumns(2);
        }
        break;
      case width >= theme.breakpointsMediaQuery.values.md:
      case width < theme.breakpointsMediaQuery.values.sm &&
        width >= theme.breakpointsMediaQuery.values.xs:
      default:
        if (columns !== 3) {
          setColumns(3);
        }
        break;
    }
  }, [columns, theme.breakpointsMediaQuery.values, width]);

  const eventsMapper = (events: Event[]): CardEventData[] =>
    events?.map<CardEventData>((event) => ({
      id: event?.id,
      title: event?.attributes?.title,
      image:
        event?.attributes?.thumbnailForEventList?.data ||
        event?.attributes?.thumbnail?.data,
      fair: event?.attributes?.fair,
      location: event?.attributes?.location,
      startDate: (event?.attributes as any)?.startDate,
      endDate: (event?.attributes as any)?.endDate,
      stand: event?.attributes?.stand,
      slug: event?.attributes?.slug || '',
      labelNation: event?.attributes?.labelNation || '',
      labelParticipation: event?.attributes?.labelParticipation || '',
      logoNation: event?.attributes?.logoNation || undefined,
      logoPartecipation: event?.attributes?.logoPartecipation || undefined,
      types: event?.attributes?.types?.data,
      url: (event?.attributes as any)?.url,
      isPrerenderRequest,
    }));

  const mappedEvents = eventsMapper(events);

  if (!events?.length) return <></>;
  return (
    <ContentWrapper>
      <SectionHeader
        subtitle={mainTitle ?? ''}
        title={title ?? ''}
        titleFontSize={titleFontSize}
        mainTitleFontSize={mainTitleFontSize}
        titleMobileFontSize={titleMobileFontSize}
        mainTitleMobileFontSize={mainTitleMobileFontSize}
        mainTitleFontWeight={mainTitleFontWeight}
        mainTitleMobileFontWeight={mainTitleMobileFontWeight}
        titleFontWeight={titleFontWeight}
        titleMobileFontWeight={titleMobileFontWeight}
      ></SectionHeader>
      {getCompany() !== Companies.SIRMAN ? (
        <>
          <HideOnMobile>
            <FlexContainer>
              <PaginationArrows
                index={currentPage}
                totalItems={Math.ceil(events.length / pageSize)}
                onLeftArrowClick={slidePrev}
                onRightArrowClick={slideNext}
              />
            </FlexContainer>
            <TableStyled>
              <TableHeader data={['', '', '', '', '', '']}></TableHeader>
              <TableBody>
                {events &&
                  events.slice(startIndex, endIndex)?.map((row, index) => {
                    return (
                      <TableBodyRowStyled
                        key={index}
                        onClick={() =>
                          router.push((row?.attributes as any)?.url)
                        }
                      >
                        <TableBodyRowCellStyled>
                          <ImageContainerStyled>
                            <ImageWithFallback
                              isPrerenderRequest={isPrerenderRequest}
                              layout="fill"
                              objectFit="contain"
                              src={getStrapiMedia(
                                row?.attributes?.thumbnailForEventList?.data
                                  ?.attributes?.url ||
                                  row?.attributes?.thumbnail?.data?.attributes
                                    ?.url ||
                                  ''
                              )}
                              alt={
                                row?.attributes?.thumbnailForEventList?.data
                                  ?.attributes?.alternativeText ||
                                row?.attributes?.thumbnail?.data?.attributes
                                  ?.alternativeText ||
                                ''
                              }
                              fallbackSrc={DEFAULT_IMAGE}
                            />
                          </ImageContainerStyled>
                        </TableBodyRowCellStyled>
                        <TableBodyRowCell>
                          <CellStyled>
                            <span>
                              {
                                translations?.pages?.newsEvents
                                  ?.pastEventsSlider?.fair
                              }
                            </span>
                            {row?.attributes?.fair ?? ''}
                          </CellStyled>
                        </TableBodyRowCell>
                        <TableBodyRowCell>
                          <CellStyled>
                            <span>
                              {
                                translations?.pages?.newsEvents
                                  ?.pastEventsSlider?.website
                              }
                            </span>
                            <Link
                              href={row?.attributes?.website || ''}
                              passHref
                            >
                              <StyledLink>
                                {row?.attributes?.website
                                  ?.replace?.(/(^\w+:|^)\/\//, '')
                                  ?.replace?.(/\/$/, '') ?? '-'}
                              </StyledLink>
                            </Link>
                          </CellStyled>
                        </TableBodyRowCell>
                        <TableBodyRowCell>
                          <CellStyled>
                            <span>
                              {
                                translations?.pages?.newsEvents
                                  ?.pastEventsSlider?.date
                              }
                            </span>
                            {row?.attributes?.startDate ?? 'N/A'} -{' '}
                            {row?.attributes?.endDate ?? 'N/A'}
                          </CellStyled>
                        </TableBodyRowCell>
                        <TableBodyRowCell>
                          <CellStyled>
                            <span>
                              {
                                translations?.pages?.newsEvents
                                  ?.pastEventsSlider?.stand
                              }
                            </span>
                            {row?.attributes?.stand ?? ''}
                          </CellStyled>
                        </TableBodyRowCell>
                        <TableBodyRowCell>
                          <CenterCellStyled>
                            <span>
                              {
                                translations?.pages?.newsEvents
                                  ?.pastEventsSlider?.participation
                              }
                            </span>
                            <InfoWithImage>
                              {row?.attributes?.logoPartecipation?.data
                                ?.attributes?.url && (
                                <ImageRowContainer>
                                  <ImageWithFallback
                                    isPrerenderRequest={isPrerenderRequest}
                                    layout="fill"
                                    objectFit="contain"
                                    src={getStrapiMedia(
                                      row?.attributes?.logoPartecipation?.data
                                        ?.attributes?.url ?? ''
                                    )}
                                    alt={
                                      row?.attributes?.logoPartecipation?.data
                                        ?.attributes?.alternativeText || ''
                                    }
                                    fallbackSrc={DEFAULT_IMAGE}
                                  />
                                </ImageRowContainer>
                              )}

                              {row?.attributes?.labelParticipation
                                ? row?.attributes?.labelParticipation
                                : row?.attributes?.logoPartecipation?.data
                                  ?.attributes?.url
                                  ? ''
                                  : '-'}
                            </InfoWithImage>
                          </CenterCellStyled>
                        </TableBodyRowCell>
                        <TableBodyRowCell>
                          <CenterCellStyled>
                            <span>
                              {
                                translations?.pages?.newsEvents
                                  ?.pastEventsSlider?.nation
                              }
                            </span>
                            <InfoWithImage>
                              {row?.attributes?.logoNation?.data?.attributes
                                ?.url && (
                                <ImageRowContainer>
                                  <ImageWithFallback
                                    isPrerenderRequest={isPrerenderRequest}
                                    layout="fill"
                                    objectFit="contain"
                                    src={getStrapiMedia(
                                      row?.attributes?.logoNation?.data
                                        ?.attributes?.url ?? ''
                                    )}
                                    alt={
                                      row?.attributes?.logoNation?.data
                                        ?.attributes?.alternativeText || ''
                                    }
                                    fallbackSrc={DEFAULT_IMAGE}
                                  />
                                </ImageRowContainer>
                              )}
                              {row?.attributes?.labelNation
                                ? row?.attributes?.labelNation
                                : row?.attributes?.logoNation?.data?.attributes
                                  ?.url
                                  ? ''
                                  : '-'}
                            </InfoWithImage>
                          </CenterCellStyled>
                        </TableBodyRowCell>
                      </TableBodyRowStyled>
                    );
                  })}
              </TableBody>
            </TableStyled>
          </HideOnMobile>
          <HideOnDesktop>
            <RepeaterWrapper>
              <Repeater
                spacing={1}
                cols={{ xs: 12, sm: 6 }}
                props={{ translations, isPrerenderRequest }}
                data={mappedEvents.slice(0, mobileItemsCount)}
                component={CardEventMobile}
              />
              {events.length > mobileItemsCount && (
                <LinkWrapper>
                  <StyledMobileLink
                    onClick={() =>
                      setMobileItemsCount(
                        mobileItemsCount + INITIAL_MOBILE_ITEM_COUNT
                      )
                    }
                  >
                    {
                      translations?.pages?.newsEvents?.pastEventsSlider
                        ?.loadNews
                    }
                  </StyledMobileLink>
                </LinkWrapper>
              )}
            </RepeaterWrapper>
          </HideOnDesktop>
        </>
      ) : (
        <>
          {mappedEvents && mappedEvents.length > 1 ? (
            <DoubleBigCardContainer>
              <CardEvent
                data={mappedEvents[0]}
                cardType={
                  width > theme.breakpointsMediaQuery.values.sm
                    ? EventCardType.BIG
                    : EventCardType.SMALL
                }
              />
              <CardEvent
                data={mappedEvents[1]}
                cardType={
                  width > theme.breakpointsMediaQuery.values.sm
                    ? EventCardType.BIG
                    : EventCardType.SMALL
                }
              />
            </DoubleBigCardContainer>
          ) : (
            <SingleBigCardContainer>
              <CardEvent
                data={mappedEvents[0]}
                cardType={
                  width > theme.breakpointsMediaQuery.values.sm
                    ? EventCardType.BIG
                    : EventCardType.SMALL
                }
              />
            </SingleBigCardContainer>
          )}
          <RepeaterWrapper>
            <Repeater
              spacing={3}
              cols={{
                md: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(3),
                sm: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(2),
                xs: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(1),
              }}
              props={{
                translations,
                isPrerenderRequest,
                cardType: EventCardType.SMALL,
              }}
              data={mappedEvents.slice(2, columns + 2)}
              component={CardEvent}
            />
          </RepeaterWrapper>
        </>
      )}
    </ContentWrapper>
  );
};

export const getServerSideProps = async (
  section: any,
  translations: any,
  slugAnalyzed: Slug,
  globalLocale: GlobalResponse
) => {
  const getNewsParams: any = {
    locale: slugAnalyzed.locale,
    datesFormat: translations?.date?.sliderNewsEvents?.format,
  };

  const types = section?.types?.data?.map?.((type: any) => type.id);

  let filters = {};
  if (types && types.length > 0) {
    filters = {
      types: {
        id: {
          $in: types,
        },
      },
    };
    getNewsParams.filters = filters;
  }

  const events = await getEvents(getNewsParams);
  section.events =
    normalizeAttributesNewsEvents(
      events,
      'events',
      translations,
      globalLocale?.attributes?.newsAndEventsMerged || false,
      slugAnalyzed.locale
    ) || [];

  section.events = section.events.sort((a: any, b: any) => {
    const { startDate: startDateA } = a.attributes || {};
    const { startDate: startDateB } = b.attributes || {};

    if (startDateA === startDateB) {
      return 0;
    } else {
      const startDateAParsed = stringToISODate(
        (startDateA as any) ?? '',
        translations?.date?.singleEvent?.format,
        slugAnalyzed?.locale
      ) as string;
      const startDateBParsed = stringToISODate(
        (startDateB as any) ?? '',
        translations?.date?.singleEvent?.format,
        slugAnalyzed?.locale
      ) as string;
      if (
        startDateAParsed &&
        startDateBParsed &&
        new Date(startDateAParsed).getTime() >
          new Date(startDateBParsed).getTime()
      ) {
        return 1;
      } else {
        return -1;
      }
    }
  });
};

export default SliderEvents;
